'use client';

import React from 'react';
import { CardComponent, Provider } from '@chargebee/chargebee-js-react-wrapper';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@benzinga/core-ui';
import { CreditCardIcon } from '@benzinga/user-ui';
import type { Card, ShopManager } from '@benzinga/shop-manager';
import { capitalize } from '@benzinga/utils';
import { SaveBar, SettingsForm } from '../_forms';
import type { SettingsFormAction, SettingsFormState } from '../_forms';
import { CHARGEBEE_PUBLISHABLE_KEY } from '../../../../src/env.client';
import { addCardAction, removeCard } from './actions';
import type { ChargebeeCardHandle } from './actions';
import styles from './Billing.module.css';

interface ChargebeeGlobal {
  getInstance: () => unknown;
  init: (options: { domain: string; publishableKey: string; site: string }) => void;
}

const getChargebee = (): ChargebeeGlobal | undefined => (window as Window & { Chargebee?: ChargebeeGlobal }).Chargebee;

interface PaymentMethodsProps {
  cards: Card[];
  isChargebeeLoaded: boolean;
  isLoading: boolean;
  shopManager: ShopManager;
}

/**
 * Why the legacy form rendered broken: (1) its wrapper was pinned to
 * `height: 20px`, clipping the hosted iframe, and (2) it called
 * `Chargebee.init()` in a mount effect while `chargebee.js` was still loading
 * (`afterInteractive`), so `window.Chargebee` was undefined, the instance
 * stayed `null`, and the fields never became ready.
 */
export function PaymentMethods({ cards, isChargebeeLoaded, isLoading, shopManager }: PaymentMethodsProps) {
  const { t } = useTranslation(['profile', 'common']);
  const cardRef = React.useRef<React.ComponentRef<typeof CardComponent>>(null);
  const areFieldsReadyRef = React.useRef(false);
  const [cbInstance, setCbInstance] = React.useState<unknown>(null);
  const [areFieldsReady, setAreFieldsReady] = React.useState(false);
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [isCardFieldFocused, setIsCardFieldFocused] = React.useState(false);
  const [cardToRemove, setCardToRemove] = React.useState<Card | null>(null);
  const [status, setStatus] = React.useState<SettingsFormState | null>(null);

  // Same init options as the legacy form — only the timing changes: it waits
  // for the script instead of racing it.
  React.useEffect(() => {
    if (!isChargebeeLoaded) return;
    const chargebee = getChargebee();
    if (!chargebee) return;
    chargebee.init({
      domain: window.location.origin,
      publishableKey: CHARGEBEE_PUBLISHABLE_KEY,
      site: 'benzinga-main',
    });
    setCbInstance(chargebee.getInstance());
  }, [isChargebeeLoaded]);

  const hasCards = cards.length > 0;
  const isFormVisible = isFormOpen || (!isLoading && !hasCards);

  const action = React.useMemo<SettingsFormAction>(() => {
    const submit = addCardAction({
      getCard: (): ChargebeeCardHandle | null => {
        const card = cardRef.current;
        if (!card || !areFieldsReadyRef.current) return null;
        return { clear: () => card.clear(), tokenize: () => card.tokenize({}) };
      },
      shopManager,
    });
    return async () => {
      const next = await submit();
      if (next.status === 'success') {
        setIsFormOpen(false);
        setStatus(next);
      }
      return next;
    };
  }, [shopManager]);

  const handleRemove = async (): Promise<void> => {
    const card = cardToRemove;
    setCardToRemove(null);
    if (!card) return;
    setStatus(await removeCard(shopManager, card.uuid));
  };

  return (
    <div className="acct-card">
      <div className={styles.cardHead}>
        <h3 className={styles.cardTitle}>
          {t('Billing.payment-method', { defaultValue: 'Payment method', ns: 'profile' })}
        </h3>
        {hasCards ? (
          <button
            className={styles.btnSecondary}
            onClick={() => {
              setStatus(null);
              setIsFormOpen(open => !open);
            }}
            type="button"
          >
            {isFormOpen
              ? t('Billing.cancel-add-card', { defaultValue: 'Cancel', ns: 'profile' })
              : t('Billing.add-card', { defaultValue: '+ Add card', ns: 'profile' })}
          </button>
        ) : null}
      </div>

      {isLoading ? (
        <div className={styles.rows}>
          <div className={styles.row}>
            <div>
              <div aria-hidden="true" className={styles.skeleton} style={{ width: '45%' }} />
              <div aria-hidden="true" className={styles.skeletonMeta} />
            </div>
          </div>
        </div>
      ) : hasCards ? (
        <div className={styles.rows}>
          {cards.map(card => (
            <div className={styles.row} key={card.uuid}>
              <div className={styles.cardIdentity}>
                <span aria-hidden="true" className={styles.cardBrand}>
                  <CreditCardIcon type={card.brand} />
                </span>
                <span className={styles.value}>
                  {capitalize(card.brand ?? '')} •••• {card.last4}
                </span>
                {card.isDefault ? (
                  <span className={`acct-pill ${styles.defaultPill}`}>
                    {t('Billing.default-card', { defaultValue: 'Default', ns: 'profile' })}
                  </span>
                ) : null}
                <span className="acct-muted">
                  {t('Billing.card-expires', { defaultValue: 'Expires', ns: 'profile' })} {card.expMonth}/{card.expYear}
                </span>
              </div>
              {/* Legacy guard: the last card on file cannot be removed — an
                  auto-renewing subscription would have nothing to charge. */}
              <button
                className={`${styles.btnSecondary} ${styles.btnDanger}`}
                disabled={cards.length === 1}
                onClick={() => setCardToRemove(card)}
                title={
                  cards.length === 1
                    ? t('Billing.last-card-locked', {
                        defaultValue: 'Add another card before removing this one.',
                        ns: 'profile',
                      })
                    : undefined
                }
                type="button"
              >
                {t('Billing.remove-card', { defaultValue: 'Remove', ns: 'profile' })}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className={styles.empty}>
          {t('Billing.no-card-on-file', {
            defaultValue: 'No card on file. Add a card to keep auto-renewing subscriptions active.',
            ns: 'profile',
          })}
        </p>
      )}

      {status?.message ? (
        <div
          className={`${styles.status} ${status.status === 'error' ? styles.statusError : styles.statusSuccess}`}
          role={status.status === 'error' ? 'alert' : 'status'}
        >
          {status.message}
        </div>
      ) : null}

      {isFormVisible ? (
        <SettingsForm action={action} className={styles.cardForm}>
          <div className={`${styles.chargebee} ${isCardFieldFocused ? styles.chargebeeFocused : ''}`}>
            {cbInstance ? (
              <Provider cbInstance={cbInstance}>
                <CardComponent
                  onBlur={() => setIsCardFieldFocused(false)}
                  onFocus={() => setIsCardFieldFocused(true)}
                  onReady={() => {
                    areFieldsReadyRef.current = true;
                    setAreFieldsReady(true);
                  }}
                  ref={cardRef}
                />
              </Provider>
            ) : null}
            {areFieldsReady ? null : (
              <span className={styles.chargebeeLoading}>
                {t('Billing.card-fields-loading', { defaultValue: 'Loading secure card fields…', ns: 'profile' })}
              </span>
            )}
            <span aria-hidden="true" className={styles.brandStrip}>
              <CreditCardIcon type="visa" />
              <CreditCardIcon type="mastercard" />
              <CreditCardIcon type="americanexpress" />
              <CreditCardIcon type="discover" />
            </span>
          </div>
          <SaveBar label={t('Billing.save-card', { defaultValue: 'Add card', ns: 'profile' })} />
        </SettingsForm>
      ) : null}

      <Dialog
        actionButtonVariant="danger"
        actionText={t('Billing.remove-card', { defaultValue: 'Remove', ns: 'profile' })}
        closeButtonVariant="flat"
        closeText={t('Billing.cancel-add-card', { defaultValue: 'Cancel', ns: 'profile' })}
        onAction={handleRemove}
        onRequestClose={() => setCardToRemove(null)}
        open={cardToRemove !== null}
        subtitle={t('Billing.remove-card-confirm', {
          defaultValue: 'Are you sure you want to remove this card?',
          ns: 'profile',
        })}
        title={t('Billing.remove-card-title', { defaultValue: 'Remove payment method', ns: 'profile' })}
      />
    </div>
  );
}
